import { Navigate, NavLink } from "react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DataTable,
  DataTableContainer,
  useDataTableController,
} from "@/shared/components/data-table";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { formatDate, formatTime } from "@/shared/utilities/date";
import { ReactElement } from "react";
import { Button } from "@/shared/components/ui/button";
import { Edit } from "lucide-react";
import { useListCountingsQuery } from "@/services/backend/countings/service";
import { Counting } from "@/services/backend/countings/countings";
import { Username } from "@/shared/components/domain/username/username";
import { FilterMode } from "@/shared/lib/filter/filter";

const tableColumns: ColumnDef<Counting>[] = [
  {
    id: "immutableReason",
    accessorKey: "immutableReason",
    header: "Festgeschrieben weil",
    cell: ({ row }) => {
      const countingData = row.original;
      return countingData.immutable ? (
        <p>{countingData.immutabilityReason as string}</p>
      ) : (
        "Nicht festgeschrieben"
      );
    },
  },
  {
    id: "immutableSetBy",
    accessorKey: "immutableSetBy",
    header: "Festgeschrieben durch",
    cell: ({ row }) => {
      const countingData = row.original;
      return countingData.immutable ? (
        <Username
          userId={countingData.immutabilitySetBy as string}
          className="items-start"
        />
      ) : (
        "Nicht festgeschrieben"
      );
    },
  },
  {
    id: "creationDate",
    accessorKey: "creationDate",
    header: "Start der Zählung",
    cell: ({ row }) => {
      const deliveryData = row.original;
      return (
        <div className="items-start">
          <div className="flex flex-col items-start">
            <span className="font-bold">
              {deliveryData.createdAt
                ? formatDate(deliveryData.createdAt!)
                : "Kein Zeiteintrag"}
            </span>
            <span>
              {deliveryData.createdAt
                ? formatTime(deliveryData.createdAt!)
                : "Kein Zeiteintrag"}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    id: "id",
    accessorKey: "id",
    header: "Details",
    cell: ({ row }) => {
      const documentationData = row.original;
      return (
        <NavLink to={documentationData.id}>
          <Button size="sm" className="-my-2">
            <Edit className="h-4 w-4" />
          </Button>
        </NavLink>
      );
    },
  },
];

export interface CountingsTableProps {
  processId: string;
  ownerId?: string;
  ownerType?: string;
}

export function CountingsTable({
  processId,
  ownerId,
  ownerType,
}: CountingsTableProps): ReactElement {
  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useListCountingsQuery(
    {
      processId: {
        active: true,
        values: [processId as string],
      },
      ownerType: {
        active: true,
        mode: ownerType ? FilterMode.Like : FilterMode.IsNull,
        values: [ownerType || ""],
      },
      ownerId: {
        active: true,
        mode: ownerId ? FilterMode.Like : FilterMode.IsNull,
        values: [ownerId || ""],
      },
      limit: 0,
      offset: 0,
    },
    { refetchOnMountOrArgChange: true },
  );

  const table = useDataTableController(tableColumns, list?.items || []);

  if (isLoading || isFetching) {
    return <div />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (list != null && list.items != null && list?.count !== 0) {
    return <Navigate to={`${list.items[0].id}`} />;
  }

  return (
    <DataTableContainer>
      <DataTable table={table} columns={tableColumns} />
    </DataTableContainer>
  );
}
